import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CybersourcePaymentForm } from 'components/CybersourceMicroformForm';
import Loading from 'components/Loading';
import usePrevious from 'hooks/usePrevious';
import formErrorsTracker from 'metrics/user-analytics/formErrorsTracker';
import { checkoutFunnelStep } from 'utils/Reserbus';

/**
 * MobileCybersourcePayment component.
 * Renders a mobile payment page using the CybersourceMicroformForm.
 *
 * @param {object} props - Component props.
 * @param {object} props.purchase - Purchase object containing payment details.
 * @param {boolean} props.fetchingPayment - Whether payment is currently being fetched.
 * @param {number} props.paymentId - ID of the payment.
 * @param {Function} props.onCybersourcePaymentAttempt - Function to process Cybersource payment.
 * @param {Function} props.transitionTo - Function to navigate to another route.
 * @param {Function} props.setupMessageListener - Function to set up message listener.
 * @param {Function} props.sendErrorMessage - Function to send error message to mobile app.
 * @param {string} props.token - Purchase token from URL.
 * @param {object} props.env - Environment configuration with currency information.
 * @param {boolean} props.isLoading - Whether the component is in loading state.
 * @param {boolean} props.isCreditCardAvailable - Whether credit card payment is available.
 * @returns {React.ReactElement} The MobileCybersourcePayment component.
 */
const MobileCybersourcePayment = ({
  purchase,
  fetchingPayment,
  paymentId,
  onCybersourcePaymentAttempt,
  transitionTo,
  setupMessageListener,
  sendErrorMessage,
  token,
  env,
  isLoading,
  isCreditCardAvailable,
}) => {
  const { total: amount, departs, returns } = purchase;
  const { currency } = env;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const prevFetchingPayment = usePrevious(fetchingPayment);
  const cybersourcePaymentFormRef = useRef(null);

  /**
   * Function to trigger payment form submission programmatically
   */
  const submitPaymentForm = useCallback(() => {
    if (cybersourcePaymentFormRef.current) {
      const formElement = cybersourcePaymentFormRef.current.querySelector(
        '#cybersource-payment-form',
      );
      if (formElement) {
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        formElement.dispatchEvent(submitEvent);
      }
    }
  }, []);

  /**
   * Set up message listener for payment check
   */
  useEffect(() => {
    /**
     * Message handler for React Native messages
     */
    const messageHandler = (message) => {
      const { action } = message.data;
      if (action === 'checkPayment') {
        submitPaymentForm();
      }
    };

    setupMessageListener(messageHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle form errors
   * @param {Array} fields - Fields with errors
   */
  const handleFormErrors = useCallback(
    (fields, formData = {}) => {
      const paymentInfo = {
        paymentType: 'credit_card',
      };

      if (formData.card_brand) {
        paymentInfo.cardBrand = formData.card_brand;
      }

      // Convert boolean errors to string messages for compatibility with formErrorsTracker
      const formattedErrors = Object.entries(fields).reduce((result, [field, hasError]) => {
        if (hasError === true) {
          // Map field names to appropriate error messages
          switch (field) {
            case 'holderName':
              result[field] = 'Required';
              break;
            case 'cardNumber':
              result[field] = 'Invalid card number';
              break;
            case 'securityCode':
              result[field] = 'Invalid security code';
              break;
            case 'expirationMonth':
            case 'expirationYear':
              result[field] = 'Invalid expiry';
              break;
            default:
              result[field] = 'Invalid field';
          }
        }
        return result;
      }, {});

      formErrorsTracker(checkoutFunnelStep(departs, returns), formattedErrors, paymentInfo);

      sendErrorMessage({ formErrors: formattedErrors });
    },
    [departs, returns, sendErrorMessage],
  );

  /**
   * Handle Cybersource payment submission
   */
  const handleCybersourceSubmit = useCallback(
    async (formData) => {
      setIsSubmitting(true);

      try {
        // Extract card information from payment data
        const cardValues = {
          billingAddress: formData.billingAddress,
        };

        // The cybersourcePayload is the data returned from the Cybersource Microform
        const cybersourcePayload = {
          card_holder_name: formData.card_holder_name,
          card_brand: formData.card_brand,
          credit_card_tokens: formData.credit_card_tokens,
        };

        // Create a single payment data object
        const paymentData = {
          purchaseToken: token,
          cardValues,
          cybersourcePayload,
          paymentInfo: { amount, currency },
        };

        // Process the payment
        await onCybersourcePaymentAttempt(paymentData);
      } catch (error) {
        console.error('Payment submission error:', error);
        sendErrorMessage('Payment submission error');
        setIsSubmitting(false);
      }
    },
    [onCybersourcePaymentAttempt, token, amount, currency, sendErrorMessage],
  );

  /**
   * Check for successful payment and navigate
   */
  useEffect(() => {
    if (!fetchingPayment && isSubmitting && paymentId) {
      transitionTo(`/payment/${token}/${paymentId}?origin=mobile-app`);
    } else if (prevFetchingPayment && !fetchingPayment && !paymentId) {
      sendErrorMessage('Payment error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchingPayment, isSubmitting, paymentId]);

  if (isLoading || !isCreditCardAvailable) {
    return <Loading />;
  }

  return (
    <div ref={cybersourcePaymentFormRef}>
      <CybersourcePaymentForm
        onSubmit={handleCybersourceSubmit}
        onFormErrors={handleFormErrors}
        billingAddress=""
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

MobileCybersourcePayment.propTypes = {
  purchase: PropTypes.shape({
    total: PropTypes.number.isRequired,
    departs: PropTypes.object,
    returns: PropTypes.object,
  }),
  token: PropTypes.string.isRequired, // Token como prop directa
  paymentId: PropTypes.number,
  fetchingPayment: PropTypes.bool.isRequired,
  onCybersourcePaymentAttempt: PropTypes.func.isRequired,
  transitionTo: PropTypes.func.isRequired,
  setupMessageListener: PropTypes.func.isRequired,
  sendErrorMessage: PropTypes.func.isRequired,
  env: PropTypes.shape({
    currency: PropTypes.string.isRequired,
  }).isRequired,
  isLoading: PropTypes.bool,
  isCreditCardAvailable: PropTypes.bool,
};

export default MobileCybersourcePayment;
